import { useMemo, useState } from "react"

import { Plus, Search } from "lucide-react"

import { ConversationItem } from "@/components/sidebar/ConversationItem"
import { useUserPhoto } from "@/hooks/useUserPhoto"
import { Skeleton } from "@/components/ui/skeleton"
import { useAppConfig } from "@/lib/app-config"
import { useChatDropTarget } from "@/lib/chat-dnd"
import { useIdentity } from "@/lib/identity"
import { initials } from "@/lib/text"
import type { Conversation, Project } from "@/types/api"
import { ProfileMenu } from "@/components/sidebar/ProfileMenu"
import { ProjectManager } from "@/components/sidebar/ProjectManager"

export interface ConversationSidebarProps {
  conversations: Conversation[]
  activeId: string | null
  loading: boolean
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onNew: () => void
  projects: Project[]
  projectsLoading: boolean
  activeProjectId: string | null
  onCreateProject: (name: string, description: string) => Promise<Project>
  onUpdateProject: (id: string, body: { name?: string; description?: string; instructions?: string }) => Promise<Project>
  onDeleteProject: (id: string, disposition: "move_to_regular" | "delete_chats") => Promise<void>
  onNewProjectChat: (projectId: string) => void
  onMoveConversation: (conversationId: string, projectId: string | null) => void
  onReferencesChanged: () => void
  onCreateDummyChats?: () => void
  /** Search mode lives in the profile menu; hidden when the backend does not offer Work IQ. */
  workIqAvailable: boolean
  workIq: boolean
  onWorkIqChange: (workIq: boolean) => void
}

/** Sidebar body — shared between the desktop pane and the mobile sheet. */
export function ConversationSidebar({
  conversations,
  activeId,
  loading,
  onSelect,
  onDelete,
  onNew,
  projects,
  projectsLoading,
  activeProjectId,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  onNewProjectChat,
  onMoveConversation,
  onReferencesChanged,
  onCreateDummyChats,
  workIqAvailable,
  workIq,
  onWorkIqChange,
}: ConversationSidebarProps) {
  const { user: account } = useIdentity()
  const photoUrl = useUserPhoto(account?.username ?? null)
  const { dev_memory_stores: devMode } = useAppConfig()
  const [query, setQuery] = useState("")
  const recentDrop = useChatDropTarget((conversationId) => {
    if (conversations.find((item) => item.id === conversationId)?.project_id) onMoveConversation(conversationId, null)
  })

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return conversations.filter((conversation) => !conversation.project_id && (!q || conversation.title.toLowerCase().includes(q)))
  }, [conversations, query])

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex flex-col gap-3 p-3">
        <button
          type="button"
          onClick={onNew}
          className="flex h-10 w-full items-center gap-2.5 rounded-lg border border-orange bg-transparent px-3.5 text-[14px] font-semibold text-foreground transition-colors hover:bg-orange hover:text-on-orange"
        >
          <Plus className="size-4" />
          New conversation
        </button>

        {devMode && onCreateDummyChats && (
          <button type="button" onClick={onCreateDummyChats} className="h-8 rounded-lg border border-dashed border-border-subtle text-[12px] text-fg3 hover:text-foreground">
            Dev: add 3 dummy chats
          </button>
        )}

        <div className="flex h-9 items-center gap-2 rounded-lg border border-border-subtle bg-elevated px-3 text-fg3 focus-within:border-orange/60">
          <Search className="size-3.5 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations"
            aria-label="Search conversations"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-foreground outline-none placeholder:text-fg3"
          />
        </div>
      </div>

      <ProjectManager
        projects={projects}
        loading={projectsLoading}
        activeProjectId={activeProjectId}
        conversations={conversations}
        activeId={activeId}
        onCreate={onCreateProject}
        onUpdate={onUpdateProject}
        onDelete={onDeleteProject}
        onNewChat={onNewProjectChat}
        onOpenConversation={onSelect}
        onDeleteConversation={onDelete}
        onMoveConversation={onMoveConversation}
        onReferencesChanged={onReferencesChanged}
      />

      <div {...recentDrop.props} className={`flex min-h-0 flex-1 flex-col ${recentDrop.over ? "bg-orange-tint ring-2 ring-orange ring-inset" : ""}`}>
      <div className="flex items-center justify-between px-4 pt-2 pb-1.5">
        <div className="text-[11px] font-bold tracking-[0.09em] text-fg3 uppercase">Recent</div>
        <span className="text-[10px] text-fg4">{filtered.length}</span>
      </div>

      <div className="flex-1 space-y-0.5 overflow-y-auto px-2 pb-3">
        {loading ? (
          Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-9 w-full rounded-md bg-overlay/60" />)
        ) : filtered.length ? (
          filtered.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              active={conversation.id === activeId}
              onSelect={() => onSelect(conversation.id)}
              onDelete={() => onDelete(conversation.id)}
              projects={projects}
              onMoveToProject={(projectId) => onMoveConversation(conversation.id, projectId)}
            />
          ))
        ) : (
          <p className="px-2.5 py-2 text-xs text-fg4">
            {query ? "No matching conversations." : "No conversations yet."}
          </p>
        )}
      </div>
      </div>

      {account && (
        <div className="m-3">
          <ProfileMenu workIqAvailable={workIqAvailable} workIq={workIq} onWorkIqChange={onWorkIqChange}>
        <div className="flex items-center gap-2.5">
          <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-overlay text-[11px] font-bold text-foreground">
            {photoUrl ? (
              <img src={photoUrl} alt="" className="size-full object-cover" />
            ) : (
              initials(account.name ?? account.username)
            )}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-semibold text-foreground">
              {account.name ?? account.username}
            </div>
            {account.name && account.username && (
              <div className="truncate text-[11px] text-fg3">{account.username}</div>
            )}
          </div>
        </div>
          </ProfileMenu>
        </div>
      )}
    </div>
  )
}
